import { SourceList } from 'constants/data';

import { useFormik } from 'formik';
import * as Yup from 'yup';
import dayjs from 'dayjs';
import clsx from 'clsx';
import { useParams } from 'react-router-dom';
import { FC, useEffect, useMemo, useState, useRef, Fragment } from 'react';
import debounce from 'lodash.debounce';
import { TableCell, TableRow } from '@mui/material';
import {
  ConfirmationModalContent,
  DatePicker,
  Dropdown,
  ErrorContainer,
  InputField,
  LoadingSpinner,
  ModalPopup,
  Table,
  TimePicker,
  Tooltip
} from 'components';
import { HeadCell } from 'components/table/table';
import { ClickAwayListener } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  deleteHealthTracker,
  getHealthTracker,
  putHealthTracker
} from 'store/healthTracker/healthTrackerActions';
import {
  updateSortsMetricList,
  deleteHealthTrackerCategory
} from 'store/healthTracker/healthTrackerSlice';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { convertLocalToUTC } from 'utils/dateHelpers/dateHelpers';

import style from './healthTracker.module.scss';
dayjs.extend(isSameOrBefore);

interface Props {
  metricId: number | string;
  resetMetricIdView: () => void;
}

const HealthTrackerTable: FC<Props> = ({ metricId, resetMetricIdView }) => {
  const { clientId } = useParams();
  const dispatch = useAppDispatch();
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [isAutosaving, setIsAutosaving] = useState<boolean | null>(null);
  const [open, setOpen] = useState(false);
  const [entered, setEntered] = useState<boolean>(false);
  const [selectedMetric, setSelectedMetric] = useState<{
    id: number | string;
    entryDate: string;
    entryTime: string | null;
    value: number | string;
    source: string;
    unit: string;
    concerningValue: boolean;
  } | null>(null);
  const { healthTrackerMetricList, healthTrackerMetricListLoading, healthTrackerCategoryList } =
    useAppSelector((state) => state.healthTracker);
  const { content, page, size } = healthTrackerMetricList;
  const tableRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (clientId && metricId) {
      dispatch(
        getHealthTracker({
          clientId,
          metricId,
          page,
          size,
          sorts: null
        })
      );
      dispatch(updateSortsMetricList(null));
    }
  }, [clientId, metricId]);

  const handleDropdownOpen = () => {
    setOpen(!open);
  };

  const handleDropdownClose = () => {
    setOpen(false);
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      ...selectedMetric,
      time: selectedMetric?.entryTime ? dayjs(selectedMetric?.entryTime).format('HH:mm') : null
    },
    validationSchema: Yup.object().shape({
      source: Yup.string().required('Source is required.'),
      entryDate: Yup.string()
        .nullable()
        .required('Date is required.')
        .test('Date', 'Date cannot be in the future.', (date) => {
          if (!date) return true;
          const now = dayjs();
          return dayjs(date).isSameOrBefore(now, 'day');
        }),
      time: Yup.string().nullable(),
      value: Yup.number()
        .typeError('Value must be a number.')
        .nullable()
        .required('Value is required.')
        .min(0, 'Value must be positive.')
        .test(
          'maxDigits',
          'Value must have at most 5 digits (e.g., 99999 or 99.999).',
          (value) => value == null || /^[0-9]{1,5}(\.[0-9]{0,4})?$/.test(value.toString())
        )
    }),
    onSubmit: (values) => {
      if (clientId && selectedMetric?.id) {
        setIsAutosaving(true);
        dispatch(putHealthTracker({ clientId, entryId: selectedMetric.id, data: values })).then(
          (res: any) => {
            if (!res.error) {
              setIsAutosaving(false);
              if (entered) {
                setEditMode(false);
                setSelectedMetric(null);
                handleDropdownClose();
                setEntered(false);
              }
            }
          }
        );
      }
    }
  });

  const headCells: HeadCell[] = [
    {
      id: 'enteredAt',
      label: 'Date/Time',
      sort: true
    },
    {
      id: 'value',
      label: 'Value',
      sort: true
    },
    {
      id: 'source',
      label: 'Source',
      sort: true
    }
  ];

  const resetMetric = () => {
    setDeleteModalOpen(false);
    setSelectedMetric(null);
    setEntered(false);
  };

  const handleDeleteMetric = async () => {
    if (clientId && selectedMetric?.id) {
      const res: any = await dispatch(
        deleteHealthTracker({ clientId, entryId: selectedMetric.id })
      );
      if (!res.error) {
        resetMetric();
        if (content.length === 1) {
          dispatch(deleteHealthTrackerCategory(metricId));
          resetMetricIdView();
        }
      }
    }
  };

  const getTableData = (data: any) => {
    if (clientId && metricId) {
      dispatch(
        getHealthTracker({
          clientId,
          metricId,
          page,
          size,
          ...data
        })
      );
    }
  };

  const debouncedAutosave = debounce(async () => {
    const errors = await formik.validateForm();
    if (Object.keys(errors).length === 0) {
      await formik.submitForm();
    }
  }, 400);

  useEffect(() => {
    if (formik.dirty) {
      debouncedAutosave();
    }
    return () => {
      debouncedAutosave.cancel();
    };
  }, [formik.values]);

  const handleFieldChange = (field: string, value: any) => {
    formik.setFieldTouched(field);
    formik.setFieldValue(field, value);
  };

  const MetricName = useMemo(
    () =>
      (healthTrackerCategoryList?.length > 0 &&
        healthTrackerCategoryList.find((a) => a.id === Number(metricId))?.code) ||
      null,
    [metricId, healthTrackerCategoryList]
  );

  useEffect(() => {
    if (!formik.values.value || !MetricName) {
      formik.setFieldValue('concerningValue', false);
      return;
    }

    const value = Number(formik.values.value);

    if (
      (MetricName === 'PHQ-9' && value >= 15) ||
      (MetricName === 'BLOOD_PRESSURE' && value > 180) ||
      (MetricName === 'BLOOD_GLUCOSE' && (value < 55 || value > 240))
    ) {
      formik.setFieldValue('concerningValue', true);
    } else {
      formik.setFieldValue('concerningValue', false);
    }
  }, [formik.values.value, metricId]);

  useEffect(() => {
    if (
      selectedMetric &&
      tableRef.current &&
      ((formik.values.value && !formik.errors.value && MetricName === 'PHQ-9') ||
        (formik.values.concerningValue && MetricName !== 'PHQ-9'))
    ) {
      const rowElement = document.getElementById(`row-${selectedMetric.id}`);

      if (rowElement) {
        const rowRect = rowElement.getBoundingClientRect();
        const tableRect = tableRef.current.getBoundingClientRect();

        if (rowRect.bottom > tableRect.bottom || rowRect.top < tableRect.top) {
          rowElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }
    }
  }, [formik.values.value, formik.errors.value, selectedMetric, content, MetricName, tableRef]);

  return (
    <>
      <Table
        ref={tableRef}
        tableWrapClassName={style.tableWrapHealthTacker}
        totalNumber={0}
        page={0}
        size={size}
        isLoading={healthTrackerMetricListLoading}
        headCells={headCells}
        getTableData={getTableData}
        handleOnSort={(_sortConfig: any) => dispatch(updateSortsMetricList(_sortConfig))}
        columns={content.map((row) => (
          <Fragment key={row.id}>
            <ClickAwayListener
              onClickAway={(event) => {
                if (editMode && selectedMetric?.id === row.id) {
                  const target = event.target as Element;
                  if (target.closest('[data-test-id="health-tracker-form-table-warning"]')) {
                    return;
                  }
                  if (
                    !target.closest(`.${style.editMode}`) ||
                    !target.closest(`.${style.inputField}`) ||
                    !target.closest(`.${style.dropdownBox}`)
                  ) {
                    setEditMode(false);
                    setSelectedMetric(null);
                    handleDropdownClose();
                  }
                }
              }}
            >
              <TableRow
                data-test-id="health-tracker-form-table"
                id={`row-${row.id}`}
                className={clsx(
                  editMode && selectedMetric?.id === row.id && style.editMode,
                  selectedMetric?.id === row.id &&
                    ((formik.values.value && !formik.errors.value && MetricName === 'PHQ-9') ||
                      (formik.values.concerningValue && MetricName !== 'PHQ-9')) &&
                    style.warningMessageMode
                )}
                onDoubleClick={() => {
                  setEditMode(true);
                  setSelectedMetric(row);
                }}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter') {
                    setEntered(false);
                  }
                  if (e.key === 'Enter') {
                    setEntered(true);
                    formik.handleSubmit();
                  }
                }}
              >
                {editMode && selectedMetric?.id === row.id ? (
                  <>
                    <TableCell>
                      <div className={style.dateTimeWrap}>
                        <div className={style.date}>
                          <DatePicker
                            classnamesProps={style.inputField}
                            onChange={(date: string) => {
                              handleFieldChange('entryDate', date);
                              if (formik.values.time && date) {
                                handleFieldChange(
                                  'entryTime',
                                  convertLocalToUTC(dayjs(date).toString(), formik.values.time)
                                );
                              }
                            }}
                            onBlur={() => formik.setFieldTouched('entryDate', true)}
                            value={formik.values.entryDate}
                            placeholder="YYYY-MM-DD"
                            maxDate={dayjs()}
                          />
                          {formik.errors.entryDate && formik.touched.entryDate && (
                            <ErrorContainer
                              autoHeight
                              visible={formik.errors.entryDate && formik.touched.entryDate}
                              name="entryDate"
                              errors={formik.errors}
                            />
                          )}
                        </div>
                        <div className={style.time}>
                          <TimePicker
                            classnamesProps={style.inputField}
                            showIcon={false}
                            name="time"
                            onChange={(time: string) => {
                              if (time === 'Invalid Date') {
                                handleFieldChange('entryTime', null);
                                handleFieldChange('time', null);
                              } else {
                                handleFieldChange(
                                  'entryTime',
                                  convertLocalToUTC(
                                    formik.values.entryDate
                                      ? dayjs(formik.values.entryDate).toString()
                                      : dayjs().toString(),
                                    time
                                  )
                                );
                                handleFieldChange('time', time);
                              }
                            }}
                            onBlur={() => {
                              formik.setFieldTouched('time');
                            }}
                            value={formik.values.time}
                          />
                          {formik.errors.time && formik.touched.time && (
                            <ErrorContainer
                              autoHeight
                              visible={formik.errors.time && formik.touched.time}
                              name="time"
                              errors={formik.errors}
                            />
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <InputField
                        classnamesProps={clsx(style.inputField, !row.unit && style.noUnitAdornment)}
                        id="value"
                        name="value"
                        type="text"
                        onChange={(event) => {
                          let inputValue = event.target.value;
                          const cursorPosition = event.target.selectionStart || 0;

                          inputValue = inputValue.replace(',', '.');

                          if (inputValue.includes('.') && inputValue.length > 6) {
                            inputValue = inputValue.substring(0, 6);
                          } else if (!inputValue.includes('.') && inputValue.length > 5) {
                            inputValue = inputValue.substring(0, 5);
                          }

                          if (cursorPosition < inputValue.length) {
                            const newChar = event.target.value[cursorPosition] || '';

                            if (
                              !(
                                inputValue[cursorPosition] === '.' &&
                                cursorPosition !== inputValue.length - 1
                              )
                            ) {
                              inputValue =
                                inputValue.substring(0, cursorPosition) +
                                newChar +
                                inputValue.substring(cursorPosition + 1);
                            }
                          }

                          const newVal =
                            inputValue.length > 5 && inputValue.substring(0, 5).includes('.');
                          if (inputValue.length > 5 && !newVal && inputValue.endsWith('.')) {
                            inputValue = inputValue.slice(0, -1);
                          }

                          const validValue = /^[0-9]{1,5}(\.[0-9]{0,4})?$/.test(inputValue);
                          if (validValue || inputValue === '') {
                            handleFieldChange('value', inputValue === '' ? null : inputValue);
                          }

                          setTimeout(() => {
                            event.target.setSelectionRange(cursorPosition, cursorPosition);
                          }, 0);
                        }}
                        onBlur={formik.handleBlur}
                        value={formik.values.value}
                        endAdornment={
                          row.unit && row.unit.length > 20 ? (
                            <Tooltip
                              toolTipText={row.unit}
                              blackTooltip
                              styles={{ marginTop: '0 !important' }}
                            >
                              <span className={style.unitAdornment}>{row.unit}</span>
                            </Tooltip>
                          ) : (
                            <span className={style.unitAdornment}>{row.unit}</span>
                          )
                        }
                      />
                      {formik.errors.value && formik.touched.value && (
                        <ErrorContainer
                          autoHeight
                          visible={formik.errors.value && formik.touched.value}
                          name="value"
                          errors={formik.errors}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <div className={style.dropdownBox}>
                        <div onClick={handleDropdownOpen} className={style.dropdownOverBox} />
                        <Dropdown
                          className={style.dropdown}
                          onChange={(event) => {
                            handleFieldChange('source', event.target.value);
                            handleDropdownClose();
                          }}
                          getOpen={(e) => {
                            if (!e && open) {
                              handleDropdownClose();
                            }
                          }}
                          onBlur={formik.handleBlur}
                          open={open}
                          data={SourceList}
                          label="Select"
                          name="source"
                          id="source"
                          value={formik.values.source?.toString() ?? ''}
                        />
                      </div>
                      {formik.errors.source && formik.touched.source && (
                        <ErrorContainer
                          autoHeight
                          visible={formik.errors.source && formik.touched.source}
                          name="source"
                          errors={formik.errors}
                        />
                      )}
                    </TableCell>
                  </>
                ) : (
                  <>
                    <TableCell>
                      <span>
                        {row.entryTime
                          ? `${dayjs(row.entryDate).format('MM/DD/YY')} ${dayjs(
                              row.entryTime
                            ).format('h:mm A')}`
                          : dayjs(row.entryDate).format('MM/DD/YY')}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className={style.valueWrap}>
                        {row.value} {row.unit}{' '}
                        {row.concerningValue && (
                          <Tooltip toolTipText="This is a concerning value" blackTooltip>
                            <span className={clsx('material-icons-round')}>warning_amber</span>
                          </Tooltip>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className={style.deleteWrap}>
                        <span>{SourceList.find((a) => a.id === row.source)?.name}</span>
                        <span
                          onClick={() => {
                            setDeleteModalOpen(true);
                            setSelectedMetric(row);
                          }}
                          className={clsx('material-icons', style.icon)}
                        >
                          delete
                        </span>
                      </div>
                    </TableCell>
                  </>
                )}
              </TableRow>
            </ClickAwayListener>
            {editMode && selectedMetric?.id === row.id && (
              <TableRow>
                {formik.values.concerningValue && MetricName !== 'PHQ-9' && (
                  <TableCell
                    colSpan={3}
                    style={{ width: '100%' }}
                    data-test-id="health-tracker-form-table-warning"
                    className={clsx(style.warningMessage, style.warningMessageTable)}
                  >
                    <span className={clsx('material-icons-round')}>warning_amber</span>This is a
                    concerning value. Please call your manager.
                  </TableCell>
                )}
                {formik.values.value && !formik.errors.value && MetricName === 'PHQ-9' && (
                  <TableCell
                    colSpan={3}
                    style={{ width: '100%' }}
                    data-test-id="health-tracker-form-table-warning"
                    className={clsx(style.warningMessage, style.warningMessageTable)}
                  >
                    <span className={clsx('material-icons-round')}>warning_amber</span>If the PHQ-9
                    score is 15 or higher or your client had thoughts of hurting themself, call your
                    manager.
                  </TableCell>
                )}
              </TableRow>
            )}
          </Fragment>
        ))}
      />
      {editMode &&
        Object.keys(formik.errors).length === 0 &&
        Object.keys(formik.touched).length !== 0 &&
        isAutosaving !== null && (
          <div className={style.autosavedBox}>
            {isAutosaving ? (
              <LoadingSpinner size={20} />
            ) : (
              <>
                <span className="material-icons">check</span>Autosaved a few seconds ago
              </>
            )}
          </div>
        )}
      {Object.keys(formik.errors).length !== 0 &&
        Object.keys(formik.touched).length !== 0 &&
        editMode && (
          <div className={clsx(style.autosavedBox, style.autosavedError)}>
            <span className="material-icons-round">warning_amber</span>Unable to autosave. There are
            invalid values.
          </div>
        )}
      <ModalPopup
        open={deleteModalOpen}
        customClassInsideContainer={style.modalDelete}
        handleClose={resetMetric}
      >
        <ConfirmationModalContent
          confirmButtonText="Delete"
          rejectButtonText="Cancel"
          title="Are you sure you want to delete this value?"
          text=" "
          handleConfirm={handleDeleteMetric}
          handleReject={resetMetric}
        />
      </ModalPopup>
    </>
  );
};

export default HealthTrackerTable;
